import { useState, useCallback, useRef, useEffect } from 'react';
import { executeAgentStream, getDisplayErrorMessage, isNetworkError } from '../services/agentService';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';
import { getAccessToken } from '../utils/authStorage';
import { Step } from '../types';

const CREDIT_NUDGE_DISMISS_KEY = 'escrituria_credit_upgrade_nudge_dismissed';

/**
 * Hook para executar agentes com subagentes e gerenciar progresso
 */
export function useAgentWithSubagents(agentCode: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string>('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [disambiguationData, setDisambiguationData] = useState<{ options: string[]; original_name: string } | null>(null);
  const [creditsExhausted, setCreditsExhausted] = useState(false);
  const [creditsInfo, setCreditsInfo] = useState<{
    usage_percent?: number;
    current_usage?: number;
    limit?: number;
    remaining?: number;
  } | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const stepsRef = useRef<Step[]>([]);
  const lastParametersRef = useRef<Record<string, any> | null>(null);
  const [canRetry, setCanRetry] = useState(false);
  const [creditUpgradeNudgeOpen, setCreditUpgradeNudgeOpen] = useState(false);
  const [creditUpgradeNudgeUsagePercent, setCreditUpgradeNudgeUsagePercent] = useState<
    number | undefined
  >(undefined);

  const dismissCreditUpgradeNudge = useCallback(() => {
    try {
      sessionStorage.setItem(CREDIT_NUDGE_DISMISS_KEY, '1');
    } catch {
      /* ignore private mode */
    }
    setCreditUpgradeNudgeOpen(false);
  }, []);

  // Timer para atualizar o tempo decorrido
  useEffect(() => {
    if (!startTime) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);


  const execute = useCallback(async (parameters: Record<string, any>) => {
    lastParametersRef.current = parameters;
    setIsLoading(true);
    setError(null);
    setCanRetry(false);
    setResult('');
    setHasStarted(true);
    setStartTime(Date.now());
    setElapsedTime(0);
    setSteps([]);
    setDisambiguationData(null);
    setCreditsExhausted(false);
    setCreditsInfo(null);
    setCreditUpgradeNudgeOpen(false);
    setCreditUpgradeNudgeUsagePercent(undefined);
    stepsRef.current = [];

    // Criar AbortController para cancelar se necessário
    abortControllerRef.current = new AbortController();

    try {
      let accumulatedText = '';
      let chunkCount = 0;
      let brokeOnError = false;
      let sawDisambiguation = false;

      for await (const event of executeAgentStream(agentCode, parameters, {
        onSteps: (initialSteps) => {
          // Converter StepProgress para Step
          const stepsData: Step[] = initialSteps.map(step => ({
            id: step.id,
            title: step.title,
            status: step.status,
            content: null,
            error: step.error,
          }));
          stepsRef.current = stepsData;
          setSteps(stepsData);
        },
        onStepStart: (step) => {
          setSteps(prev => {
            const updated = prev.map(s =>
              s.id === step.id ? { ...s, status: 'running' as const } : s
            );
            stepsRef.current = updated;
            return updated;
          });
        },
        onStepSuccess: (step) => {
          setSteps(prev => {
            const updated = prev.map(s =>
              s.id === step.id ? { ...s, status: 'success' as const } : s
            );
            stepsRef.current = updated;
            return updated;
          });
        },
        onStepError: (step) => {
          setSteps(prev => {
            const updated = prev.map(s =>
              s.id === step.id ? { ...s, status: 'error' as const, error: step.error } : s
            );
            stepsRef.current = updated;
            return updated;
          });
          setError(`Erro no subagente "${step.title}": ${step.error}`);
        },
        onChunk: (chunk) => {
          if (!chunk || chunk.trim().length === 0) {
            // Ignorar chunks vazios mas não incrementar contador
            return;
          }
          chunkCount++;
          accumulatedText += chunk;
          // Atualizar o conteúdo do step que está fazendo streaming
          setSteps(prev => {
            const runningStep = prev.find(s => s.status === 'running');
            if (runningStep) {
              const updated = prev.map(s =>
                s.id === runningStep.id ? { ...s, content: accumulatedText } : s
              );
              stepsRef.current = updated;
              return updated;
            }
            return prev;
          });
          // Atualizar resultado em tempo real também
          setResult(accumulatedText);
        },
        onDone: () => {
          // Sempre finalizar loading
          setIsLoading(false);
          
          // Tentar obter resultado do accumulatedText primeiro
          if (accumulatedText.trim().length > 0) {
            setResult(accumulatedText);
          } else {
            // Fallback: buscar conteúdo do último step que tenha conteúdo
            const lastStepWithContent = stepsRef.current
              .slice()
              .reverse()
              .find(step => step.content && step.content.trim().length > 0);
            
            if (lastStepWithContent?.content) {
              setResult(lastStepWithContent.content);
            }
          }
          
          // Atualizar último step como sucesso
          setSteps(prev => {
            if (prev.length > 0) {
              const lastStep = prev[prev.length - 1];
              const finalContent = accumulatedText.trim() || lastStep.content || '';
              const updated = prev.map(s =>
                s.id === lastStep.id 
                  ? { ...s, content: finalContent, status: 'success' as const } 
                  : s
              );
              stepsRef.current = updated;
              return updated;
            }
            return prev;
          });
        },
        onError: (errorMessage) => {
          // Não setar erro aqui se for créditos esgotados - será tratado no evento
          // O erro será setado apenas se não for créditos esgotados
          if (!errorMessage.includes('Créditos esgotados')) {
            setError(errorMessage);
          }
        },
        onCreditsExhausted: (creditsInfo) => {
          // Será tratado no evento do stream
        },
        onDisambiguationRequired: (data) => {
          setDisambiguationData(data);
          setIsLoading(false); // Pausar loading quando desambiguação é necessária
        },
      })) {
        // Processar eventos já foi feito nos callbacks acima
        if (event.type === 'error') {
          brokeOnError = true;
          if (event.creditsExhausted) {
            setCreditsExhausted(true);
            setCreditsInfo(event.creditsInfo || null);
            setError(null);
            setCanRetry(false);
            setIsLoading(false);
          } else {
            const displayMsg = getDisplayErrorMessage(event.error || 'Erro desconhecido');
            setError(displayMsg);
            setCanRetry(Boolean(event.isNetworkError));
          }
          break;
        }
        if (event.type === 'disambiguation_required') {
          sawDisambiguation = true;
          // Já processado no callback, apenas garantir que loading está pausado
          setIsLoading(false);
        }
        // NOTA: Não acumulamos chunks aqui porque já são processados no callback onChunk
        // Acumular aqui causaria DUPLICAÇÃO do texto
      }

      if (!brokeOnError && !sawDisambiguation) {
        try {
          if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CREDIT_NUDGE_DISMISS_KEY)) {
            /* skip nudge */
          } else {
            const token = getAccessToken();
            if (token) {
              const res = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.USAGE_SUMMARY}`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });
              if (res.ok) {
                const data = await res.json();
                const usage = data.usage;
                if (usage) {
                  const unlimited = usage.credits?.unlimited || usage.tokens?.unlimited;
                  const p = usage.credits?.usage_percent ?? usage.tokens?.usage_percent ?? 0;
                  if (!unlimited && p >= 85 && p < 100) {
                    setCreditUpgradeNudgeUsagePercent(p);
                    setCreditUpgradeNudgeOpen(true);
                  }
                }
              }
            }
          }
        } catch {
          /* ignore */
        }
      }
    } catch (err: any) {
      const errorMessage = getDisplayErrorMessage(err?.message || 'Erro ao executar o agente');
      setError(errorMessage);
      setCanRetry(isNetworkError(err?.message || ''));
    } finally {
      setIsLoading(false);
    }
  }, [agentCode]);

  const reset = useCallback(() => {
    setIsLoading(false);
    setError(null);
    setCanRetry(false);
    setResult('');
    setSteps([]);
    setHasStarted(false);
    setStartTime(null);
    setElapsedTime(0);
    setDisambiguationData(null);
    setCreditsExhausted(false);
    setCreditsInfo(null);
    setCreditUpgradeNudgeOpen(false);
    setCreditUpgradeNudgeUsagePercent(undefined);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const onRetry = useCallback(() => {
    if (lastParametersRef.current) {
      execute(lastParametersRef.current);
    }
  }, [execute]);

  return {
    isLoading,
    error,
    result,
    steps,
    hasStarted,
    elapsedTime,
    disambiguationData,
    execute,
    reset,
    creditsExhausted,
    creditsInfo,
    setCreditsExhausted,
    canRetry,
    onRetry,
    creditUpgradeNudgeOpen,
    creditUpgradeNudgeUsagePercent,
    dismissCreditUpgradeNudge,
  };
}

